import { useCallback, useEffect, useState } from 'react';

export type ScanPhotoBitmap =
    | { status: 'loading' }
    | { status: 'ready'; bitmap: ImageBitmap }
    | { status: 'error'; message: string };

const LOAD_ERROR =
    'Could not load this photo. Its link may have expired; go back and pick it again.';

// no-store skips a copy the carousel cached without CORS headers, which would taint the canvas.
async function fetchPhotoBitmap(url: string, signal: AbortSignal): Promise<ImageBitmap> {
    const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal
    });
    if (!response.ok) throw new Error(`Photo request failed with ${response.status}`);
    return createImageBitmap(await response.blob());
}

export function useScanPhotoBitmap(url: string): {
    photo: ScanPhotoBitmap;
    retry: () => void;
} {
    const [photo, setPhoto] = useState<ScanPhotoBitmap>({ status: 'loading' });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        let loaded: ImageBitmap | null = null;
        setPhoto({ status: 'loading' });
        fetchPhotoBitmap(url, controller.signal)
            .then((bitmap) => {
                if (controller.signal.aborted) {
                    bitmap.close();
                    return;
                }
                loaded = bitmap;
                setPhoto({ status: 'ready', bitmap });
            })
            .catch(() => {
                if (!controller.signal.aborted) setPhoto({ status: 'error', message: LOAD_ERROR });
            });
        return () => {
            controller.abort();
            loaded?.close();
        };
    }, [url, attempt]);

    const retry = useCallback(() => setAttempt((count) => count + 1), []);
    return { photo, retry };
}
